import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plugs, Plus, Code, Copy, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ConversionPostback() {
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pixelDialog, setPixelDialog] = useState(null);
  const [form, setForm] = useState({ offer_id: '', postback_url: '', event_type: 'conversion' });

  useEffect(() => {
    Promise.all([api.get('/postback/configs'), api.get('/postback/logs')])
      .then(([c, l]) => { setConfigs(c.data); setLogs(l.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const createConfig = async (e) => {
    e.preventDefault();
    try {
      await api.post('/postback/configure', form);
      toast.success('Postback configured');
      setDialogOpen(false);
      setForm({ offer_id: '', postback_url: '', event_type: 'conversion' });
      const { data } = await api.get('/postback/configs');
      setConfigs(data);
    } catch (err) { toast.error('Failed to configure postback'); }
  };

  const viewPixel = async (offerId) => {
    try {
      const { data } = await api.get(`/postback/pixel/${offerId}`);
      setPixelDialog(data);
    } catch (err) { toast.error('Failed to get pixel code'); }
  };

  const copyCode = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Copied to clipboard');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="postback-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Plugs size={32} className="text-blue-400" weight="duotone" />
            Conversion Postback
          </h1>
          <p className="text-sm text-zinc-500 mt-1">S2S postback URLs and pixel/iframe tracking</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-postback-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus size={16} /> Add Postback</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <DialogHeader><DialogTitle className="font-heading text-white">Configure Postback</DialogTitle></DialogHeader>
            <form onSubmit={createConfig} className="space-y-4">
              <div><Label className="text-zinc-300">Offer ID</Label><Input data-testid="pb-offer-id" value={form.offer_id} onChange={e => setForm(p => ({ ...p, offer_id: e.target.value }))} required placeholder="OFF-XXXXXXXX" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" /></div>
              <div>
                <Label className="text-zinc-300">Postback URL</Label>
                <Input data-testid="pb-url" value={form.postback_url} onChange={e => setForm(p => ({ ...p, postback_url: e.target.value }))} required
                  placeholder="https://your-server.com/postback?click_id={click_id}&revenue={revenue}" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100" />
                <p className="text-[10px] text-zinc-500 mt-1">Macros: {'{click_id}'}, {'{revenue}'}, {'{offer_id}'}, {'{affiliate_id}'}</p>
              </div>
              <div>
                <Label className="text-zinc-300">Event Type</Label>
                <select data-testid="pb-event-type" value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))} className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-100 text-sm">
                  <option value="conversion">Conversion</option>
                  <option value="click">Click</option>
                  <option value="install">Install</option>
                </select>
              </div>
              <Button data-testid="submit-postback" type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">Save Configuration</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white">Active Postback Configurations</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Config ID</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Offer</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Type</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Fires</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Pixel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((c, i) => (
                <TableRow key={i} className="border-zinc-800/50" data-testid={`postback-config-${i}`}>
                  <TableCell className="text-zinc-500 font-mono text-xs">{c.config_id}</TableCell>
                  <TableCell className="text-zinc-200">{c.offer_id}</TableCell>
                  <TableCell><Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">{c.event_type}</Badge></TableCell>
                  <TableCell className="text-zinc-300">{c.total_fires || 0}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${c.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-700/30 text-zinc-400'}`}>{c.status}</Badge></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => viewPixel(c.offer_id)} data-testid={`view-pixel-${i}`} className="text-zinc-400 hover:text-blue-400 gap-1 text-xs"><Code size={14} /> Pixel</Button>
                  </TableCell>
                </TableRow>
              ))}
              {configs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No postback configurations yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {pixelDialog && (
        <Card className="bg-zinc-900 border-zinc-800 border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Code size={18} className="text-blue-400" /> Tracking Code — {pixelDialog.offer_id}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setPixelDialog(null)} className="text-zinc-400">Close</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: 'S2S Postback URL', code: pixelDialog.s2s_url },
              { label: 'Image Pixel', code: pixelDialog.pixel_html },
              { label: 'iFrame Pixel', code: pixelDialog.iframe_html },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-zinc-400 font-medium">{item.label}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyCode(item.code)} className="text-zinc-400 hover:text-blue-400 gap-1 text-xs" data-testid={`copy-code-${i}`}><Copy size={12} /> Copy</Button>
                </div>
                <pre className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-xs text-blue-400 font-mono overflow-x-auto">{item.code}</pre>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-lg text-white flex items-center gap-2"><ArrowsClockwise size={20} className="text-emerald-400" /> Recent Postback Fires</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Config</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">URL</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.slice(0, 15).map((l, i) => (
                <TableRow key={i} className="border-zinc-800/50">
                  <TableCell className="text-zinc-500 font-mono text-xs">{l.config_id}</TableCell>
                  <TableCell className="text-zinc-300 text-xs max-w-[300px] truncate">{l.url}</TableCell>
                  <TableCell><Badge className="bg-emerald-500/10 text-emerald-400 text-[10px]">{l.status}</Badge></TableCell>
                  <TableCell className="text-zinc-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-zinc-500 py-8">No postback fires yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
